import { z } from "zod";
import {
  PAYMENT_METHODS,
  TOR_ACTIONS,
  type ContractStatus,
  type PaymentMethod,
  type TorAction,
  type TorChangeAction,
  type TorStatus,
} from "../enums";
import { LIMITS } from "../limits";
import type { TorDetailExtraV2Fields, TorSummaryV2Fields } from "./v1-additions";
import {
  type ActorRef,
  type UserRef,
  cents,
  checkDateOrder,
  isoDate,
  optionalText,
  requiredText,
  uuid,
} from "./common";
import type { CostLine } from "./cost-lines";

// ---------- Sections (ARCHITECTURE 7) ----------

export const OBJECTIVE_KEY = "objective";
export const CUSTOM_SECTION_PREFIX = "custom-";

export const blockSchema = z.object({
  id: uuid,
  text: z.string().max(LIMITS.blockText, "A text block can hold at most 20,000 characters."),
  internal: z.boolean(),
});
export type Block = z.infer<typeof blockSchema>;

export const teamSectionSchema = z.object({
  key: z
    .string()
    .refine(
      (k) => k === OBJECTIVE_KEY || /^custom-[0-9a-f-]{36}$/i.test(k),
      'A section key is "objective" or "custom-<uuid>".',
    ),
  title: requiredText(LIMITS.sectionTitle, "Section title"),
  blocks: z.array(blockSchema).max(LIMITS.blocksPerSection, "At most 50 text blocks per section."),
});
export type TeamSection = z.infer<typeof teamSectionSchema>;

export const sectionsSchema = z.array(teamSectionSchema).max(LIMITS.sectionsMax, "At most 20 sections.");

// ---------- Requests ----------

const torFields = {
  title: requiredText(LIMITS.torTitle, "Title"),
  activityCode: optionalText(LIMITS.activityCode, "Activity code"),
  paymentMethod: z.enum(PAYMENT_METHODS),
  location: optionalText(LIMITS.location, "Location"),
  startDate: isoDate.nullable(),
  endDate: isoDate.nullable(),
  participants: optionalText(LIMITS.participants, "Participants"),
};

export const createTorBody = z
  .object({
    contractId: uuid,
    title: torFields.title,
    activityCode: torFields.activityCode.default(""),
    paymentMethod: torFields.paymentMethod,
    location: torFields.location.default(""),
    startDate: torFields.startDate.default(null),
    endDate: torFields.endDate.default(null),
    participants: torFields.participants.default(""),
  })
  .strict()
  .superRefine(checkDateOrder);
export type CreateTorBody = z.input<typeof createTorBody>;
export type CreateTorInput = z.output<typeof createTorBody>;

export const updateTorBody = z
  .object({
    title: torFields.title.optional(),
    activityCode: torFields.activityCode.optional(),
    paymentMethod: torFields.paymentMethod.optional(),
    location: torFields.location.optional(),
    startDate: torFields.startDate.optional(),
    endDate: torFields.endDate.optional(),
    participants: torFields.participants.optional(),
    budgetCents: cents(LIMITS.budgetCentsMax).optional(),
    sections: sectionsSchema.optional(),
    /** v2 (API.md 6.2): the logframe result this ToR contributes to; a result of the same contract */
    resultId: uuid.nullable().optional(),
  })
  .strict()
  .superRefine(checkDateOrder);
export type UpdateTorBody = z.infer<typeof updateTorBody>;

export const torTransitionBody = z
  .object({
    action: z.enum(TOR_ACTIONS),
    note: optionalText(LIMITS.transitionNote, "Note").optional(),
  })
  .strict();
export type TorTransitionBody = z.infer<typeof torTransitionBody>;

export const torCommentBody = z
  .object({ text: requiredText(LIMITS.comment, "Comment") })
  .strict();
export type TorCommentBody = z.infer<typeof torCommentBody>;

// ---------- Responses ----------

export type TorSummary = {
  id: string;
  contractId: string;
  title: string;
  activityCode: string;
  paymentMethod: PaymentMethod;
  location: string;
  startDate: string | null;
  endDate: string | null;
  budgetCents: number;
  status: TorStatus;
  version: number;
  createdBy: ActorRef;
  submittedAt: string | null;
  decidedAt: string | null;
  /** whole days since submittedAt while status = submitted, else null */
  daysWithFunder: number | null;
  updatedAt: string;
} & TorSummaryV2Fields;

export type TorChange = {
  id: string;
  action: TorChangeAction;
  fromStatus: TorStatus | null;
  toStatus: TorStatus | null;
  version: number;
  note: string | null;
  actor: ActorRef;
  occurredAt: string;
};

export type TorVersionRef = { version: number; submittedAt: string; submittedBy: ActorRef };

export type TorDetail = TorSummary & {
  participants: string;
  sections: TeamSection[];
  /** by position */
  costLines: CostLine[];
  /** costLines.length > 0 */
  budgetFromLines: boolean;
  /** any line with unitCostCents null */
  missingUnitCosts: boolean;
  signedOffAt: string | null;
  withdrawnAt: string | null;
  createdAt: string;
  contract: {
    id: string;
    name: string;
    reference: string;
    status: ContractStatus;
    lead: UserRef | null;
    funderContactName: string;
    funderContactEmail: string;
    waitThresholdDays: number;
  };
  /** for the expert-days picker */
  experts: { id: string; name: string; positionTitle: string; feeRateCents: number | null }[];
  /** newest first */
  changes: TorChange[];
  /** oldest first */
  versions: TorVersionRef[];
  lastRevisionNote: string | null;
  /** for this user, this status, lead flag, open contract */
  allowedActions: TorAction[];
  can: { edit: boolean; editBudget: boolean; delete: boolean; comment: boolean; export: boolean };
} & TorDetailExtraV2Fields;

// ---------- The composed document ----------

export type DocParagraph = { text: string; muted: boolean };
export type DocTable = { columns: string[]; rows: string[][]; totalRow: string[] };
export type DocSection = {
  number: number;
  /** "background" | "objective" | "activity" | "budget" | "reporting" | "custom-…" */
  key: string;
  title: string;
  source: "record" | "team";
  /** budget section: paragraphs[0] = total line; the table sits between paragraphs[0] and the rest */
  paragraphs: DocParagraph[];
  /** only the budget section, when it has lines */
  table: DocTable | null;
};
export type TorDocument = {
  orgName: string;
  /** "Terms of Reference: {tor.title}" */
  title: string;
  /** "{contract.name} · {reference} · Version {n}" */
  meta: string;
  status: TorStatus;
  version: number;
  /** status not in draft/withdrawn */
  isFinal: boolean;
  sections: DocSection[];
  /** "{reference} · generated {12 March 2027}" */
  footer: string;
  generatedAt: string;
};

export type TorSnapshot = {
  version: number;
  submittedAt: string;
  submittedBy: ActorRef;
  tor: {
    title: string;
    activityCode: string;
    paymentMethod: PaymentMethod;
    location: string;
    startDate: string | null;
    endDate: string | null;
    participants: string;
    budgetCents: number;
  };
  /** including internal blocks, as stored */
  sections: TeamSection[];
  costLines: CostLine[];
  /** exactly what was sent (internal blocks excluded) */
  document: TorDocument;
  /** v3: the typed fields as sent, from-the-record values filled (absent on v1/v2 versions). */
  fields?: {
    key: string;
    title: string;
    fields: { key: string; label: string; kind: "short_text" | "long_text" | "list" | "experts"; value: string | string[]; fromRecord: { text: string; link: { label: string; path: string } | null } | null }[];
  }[];
};
